<script setup lang="ts">
import TicketScannerDevice from '~/components/pages/home/ticket-scanner/TicketScannerDevice.vue'
import { useUserLotteryStore } from '~/store/scan/lottery'
import { useAuthStore } from '~/store/auth'
import { trackLandingScannerClick, trackAuthModalOpen, trackAuthSuccessRedirect } from '~/tracking/scan/scanSection'
import { SHOW_TAB_SCAN_TICKET_ALWAYS } from '~/config/featureFlags'
import { trackAlternativeFlowDetected } from '~/tracking/scan/scanEmptyEvents'

const ModalSignUpTicket = defineAsyncComponent(() => import('~/components/modals/ModalSignUpTicket.vue'))
const ModalLoginTicket = defineAsyncComponent(() => import('~/components/modals/ModalLoginTicket.vue'))

const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)
const userLotteryStore = useUserLotteryStore()
const localePath = useLocalePath()
const authStore = useAuthStore()
const { loggedIn } = storeToRefs(authStore)
const { t: $t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

let redirectTimer: ReturnType<typeof setTimeout> | null = null

// --- Animation state ---
const isScanning = ref(false)
const isFlipped = ref(false)
const showFeatures = ref(false)

// --- Intersection ---
const sectionRef = ref<HTMLElement | null>(null)
let hasAnimated = false // solo anima una vez
let observer: IntersectionObserver | null = null

let t1: ReturnType<typeof setTimeout> | null = null
let t2: ReturnType<typeof setTimeout> | null = null

const textHtml = computed(() => {
	return $t('Create a {lottery} to scan, track, and get alerts on your physical tickets.', {
		lottery: `<span class="font-semibold text-primary-500">${$t('free account ')}</span>`,
	})
})

const getLogin = computed(() => loggedIn.value)

const getTitle = computed(() => {
	if (locale.value === 'fr') return `<span class="text-green-500">Scanner</span> de Billets Automatique`
	else if (locale.value === 'pt') return `<span class="text-green-500">Leitor</span> Automático de Bilhetes`
	return `Automatic Ticket <span class="text-green-500">Scanner</span>`
})

const clearTimers = () => {
	if (t1) clearTimeout(t1)
	if (t2) clearTimeout(t2)
}

const restart = () => {
	clearTimers()
	isFlipped.value = false
	showFeatures.value = false
	isScanning.value = false

	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			isScanning.value = true
			t1 = setTimeout(() => {
				isScanning.value = false
				isFlipped.value = true
				t2 = setTimeout(() => {
					showFeatures.value = true
				}, 900)
			}, 2600)
		})
	})
}

const openScanTicket = async () => {
	trackLandingScannerClick({
		cta_location: 'scanner_section',
	})
	trackAlternativeFlowDetected({
		missing_steps: ['country', 'lottery', 'numbers'].join(','),
	})
	const basePath = localePath('/results/scan')
	if ($hasFeature(SHOW_TAB_SCAN_TICKET_ALWAYS) === true) {
		await navigateTo(`${basePath}`, { replace: true })
		return
	}
	if (getLogin.value === false) {
		toggleAuthGoogle(true)
		trackAuthModalOpen({
			source: 'hero',
			default_tab: 'signup',
		})
		return
	}

	await navigateTo(`${basePath}`, { replace: true })
}

const toggleAuthGoogle = (value: boolean) => {
	userLotteryStore.SET_STATE_LOTTERY({
		type: 'showModalSignUp',
		value: value,
	})
}

const isLocalePrefixed = (p: string) => /^\/(pt|fr)(\/|$)/.test(p)

const withCurrentLocale = (p: string) => {
	if (isLocalePrefixed(p)) return p
	return localePath(p)
}

const getRedirectTarget = () => {
	const fromQuery = safeInternalPath(route.query.redirect)
	if (fromQuery) return withCurrentLocale(fromQuery)

	const fromSession = getLastInternalPath()
	if (fromSession) return withCurrentLocale(fromSession)

	return localePath('/')
}

const sendAfterValidation = () => {
	if (redirectTimer) return
	trackAuthSuccessRedirect({
		source: 'scanner_section',
		redirect_destination: 'scan_ticket',
	})

	const target = getRedirectTarget()
	clearLastInternalPath()

	redirectTimer = setTimeout(() => {
		router.replace(target)
	}, 5000)
}

const closeAuthGoogle = () => {
	toggleAuthGoogle(false)
	sendAfterValidation()
}

const closeAuthGoogleModalLogin = () => {
	sendAfterValidation()
}

onMounted(() => {
	observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0]
			if (entry.isIntersecting && !hasAnimated) {
				hasAnimated = true
				restart()
				observer?.disconnect() // deja de observar una vez que animó
			}
		},
		{ threshold: 0.3 }, // se activa cuando el 30% del componente es visible
	)

	if (sectionRef.value) {
		observer.observe(sectionRef.value)
	}
})

onUnmounted(() => {
	clearTimers()
	observer?.disconnect()
})
</script>

<template>
	<section
		ref="sectionRef"
		class="scanner-section relative overflow-hidden flex items-center justify-center"
	>
		<NuxtImg
			src="/cdn/background_scan_ticket_mobile.webp"
			alt="Background Scan Ticket"
			format="webp"
			class="absolute block smm:hidden inset-0 h-full w-full object-cover object-left-bottom sm:object-center pointer-events-none select-none"
		/>
		<NuxtImg
			src="/cdn/background_scan_ticket.webp"
			alt="Background Scan Ticket"
			format="webp"
			class="hidden smm:block absolute inset-0 h-full w-full object-cover object-left-bottom sm:object-right lg:object-center pointer-events-none select-none"
		/>
		<div class="container relative z-10 py-16 sm:py-24">
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
				<!-- LEFT: Scanner animation -->
				<TicketScannerDevice
					:is-scanning="isScanning"
					:is-flipped="isFlipped"
					:show-features="showFeatures"
					@restart="restart"
				/>

				<!-- RIGHT: Text content -->
				<div class="flex flex-col items-start">
					<div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/20 mb-6">
						<span class="w-2 h-2 rounded-full bg-green-500" />
						<span class="text-sm font-medium text-green-500 tracking-wide">{{ $t('New Feature') }}</span>
					</div>

					<h2 class="text-3xl smm:text-4xl md:text-5xl font-semibold text-white leading-tight mb-2">
						<span v-html="getTitle" />
					</h2>

					<p class="text-sm smm:text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-10 max-w-md">
						{{ $t(`Upload a photo or enter numbers manually to check your tickets. We’ll track the results for you`) }}.
					</p>

					<button
						class="flex justify-center items-center gap-2 px-5 py-2 rounded-full border border-primary-400 text-primary-400 font-semibold text-sm hover:bg-primary/10 transition-colors min-w-60 min-h-12 mx-auto sm:mx-0"
						@click="openScanTicket"
					>
						<UIcon
							name="i-mdi:line-scan"
							class="w-4 h-4"
						/>
						{{ $t('Try the Scanner') }}
					</button>
				</div>
			</div>
			<ModalSignUpTicket
				v-model="userLotteryStore.showModalSignUp"
				@close-auth-google="closeAuthGoogle"
			>
				<template #header>
					<div class="space-y-4 text-center">
						<div class="w-16 h-16 mx-auto rounded-full bg-[#18212F] flex items-center justify-center">
							<UIcon
								name="i-mono-icons:unlock"
								class="size-10 text-primary-400 scale-x-[-1]"
							/>
						</div>
						<div class="text-neutral-50 space-y-2.5 text-center">
							<h1 class="text-xl sm:text-3xl font-bold">{{ $t('Unlock the Ticket Scanner') }}</h1>
							<p
								class="text-base text-neutral-50"
								v-html="textHtml"
							></p>
						</div>
					</div>
				</template>
			</ModalSignUpTicket>
			<ModalLoginTicket
				v-model="userLotteryStore.showModalLogin"
				@close-auth-google="closeAuthGoogleModalLogin"
			/>
		</div>
	</section>
</template>

<style>
.scanner-section {
	background:
		linear-gradient(to bottom right, #00300d 0%, #2d4b36 50%) bottom right / 50% 50% no-repeat,
		linear-gradient(to bottom left, #00300d 0%, #2d4b36 50%) bottom left / 50% 50% no-repeat,
		linear-gradient(to top left, #00300d 0%, #2d4b36 50%) top left / 50% 50% no-repeat,
		linear-gradient(to top right, #00300d 0%, #2d4b36 50%) top right / 50% 50% no-repeat;
}
</style>
